import {
  ViewabilityEventConditions,
  ViewabilityEnvironment
} from '../../../enums';
import { ViewabilityObserver } from '../../../types/viewability-observer';

const VIEWABLE_THRESHOLD_49_PERCENTS = 0.49;
const VIEWABLE_THRESHOLD_50_PERCENTS = 0.5;
const VIEWABLE_THRESHOLD_99_PERCENTS = 0.99;
const VIEWABLE_THRESHOLD_100_PERCENTS = 1;

export class IntersectionObserverService implements ViewabilityObserver {
  readonly environment: ViewabilityEnvironment = ViewabilityEnvironment.Web;
  private readonly listeners: Record<
    ViewabilityEventConditions,
    Array<() => void>
  > = {
    [ViewabilityEventConditions.OnScreen]: [],
    [ViewabilityEventConditions.On50PercentVisible]: [],
    [ViewabilityEventConditions.On100PercentVisible]: [],
    [ViewabilityEventConditions.OnNotVisible]: []
  };
  private observer: IntersectionObserver | null = null;

  observe(element: HTMLElement) {
    this.disconnect();
    this.observer = new IntersectionObserver(
      ([entry]) => this.checkIntersection(entry),
      {
        threshold: [
          VIEWABLE_THRESHOLD_49_PERCENTS,
          VIEWABLE_THRESHOLD_50_PERCENTS,
          VIEWABLE_THRESHOLD_99_PERCENTS,
          VIEWABLE_THRESHOLD_100_PERCENTS
        ]
      }
    );
    this.observer.observe(element);
  }

  on(event: ViewabilityEventConditions, callback: () => void) {
    this.listeners[event].push(callback);
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private checkIntersection(entry: {
    isIntersecting: boolean;
    intersectionRatio: number;
  }): void {
    if (entry.isIntersecting) {
      this.callListeners(ViewabilityEventConditions.OnScreen);
    }

    if (
      !entry.isIntersecting ||
      entry.intersectionRatio < VIEWABLE_THRESHOLD_50_PERCENTS
    ) {
      this.callListeners(ViewabilityEventConditions.OnNotVisible);
    } else if (
      entry.intersectionRatio >= VIEWABLE_THRESHOLD_50_PERCENTS &&
      entry.intersectionRatio < VIEWABLE_THRESHOLD_100_PERCENTS
    ) {
      this.callListeners(ViewabilityEventConditions.On50PercentVisible);
    } else if (entry.intersectionRatio === VIEWABLE_THRESHOLD_100_PERCENTS) {
      this.callListeners(ViewabilityEventConditions.On50PercentVisible);
      this.callListeners(ViewabilityEventConditions.On100PercentVisible);
    }
  }

  private callListeners(condition: ViewabilityEventConditions): void {
    this.listeners[condition].forEach(cb => cb());
  }
}
